import { useState, type DragEvent, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, AlertTriangle, Circle, MoreVertical } from "lucide-react";
import type { DealListItemDto } from "@/types/deals";
import { Badge } from "@/design-system/components/ui";
import { BrokerRecordLink } from "@/components/crosslinks/BrokerCrossLinks";
import { cn } from "@/design-system/lib/utils";
import { DealTypeBadge } from "./DealTypeBadge";
import {
  ageBadgeStatus,
  ageSeverity,
  dealLabel,
  formatCurrency,
  formatSlaBadge,
  isReadyToAdvance,
} from "./dealFormat";

export interface DealCardAction {
  key: string;
  label: string;
  onClick: (deal: DealListItemDto) => void;
  destructive?: boolean;
  hidden?: boolean;
}

export interface DealCardProps {
  deal: DealListItemDto;
  draggable?: boolean;
  /** Optional context-menu actions (Decline, Withdraw, Reassign, Archive).
   *  Each action's `hidden` flag is honoured before render, so callers can
   *  feed permission-gated lists without filtering. */
  actions?: DealCardAction[];
  /** Brokers / counterparties / users displayed as labels — caller looks
   *  these up via the relevant cache to keep the card a pure presentation
   *  component. */
  brokerName?: string | null;
  assigneeName?: string | null;
  /** Optional badge indicators set by the dashboard / Kanban. The card has
   *  no opinion on what counts as overdue or missing — those are computed
   *  upstream from the document-requests + checklist queries. */
  hasOverdueDocs?: boolean;
  hasMissingRequiredFields?: boolean;
}

/** Kanban + dashboard list card. Drag is enabled when `draggable` is true;
 *  the component sets `dataTransfer.effectAllowed = "move"` and writes the
 *  deal id as JSON so multiple drop targets can read it without coupling. */
export function DealCard({
  deal,
  draggable = false,
  actions,
  brokerName,
  assigneeName,
  hasOverdueDocs,
  hasMissingRequiredFields,
}: DealCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragging, setDragging] = useState(false);

  const severity = ageSeverity(deal);
  const slaText = formatSlaBadge(deal);
  const ready = isReadyToAdvance(deal.subState);
  const visibleActions = (actions ?? []).filter((a) => !a.hidden);

  // Decline-state card styling. A ratified-Declined
  // deal renders muted (50% opacity) with a strikethrough title + red
  // badge; a DeclineProposed deal gets a yellow proposal overlay banner.
  const isDeclined = deal.declineStatus === "Declined";
  const isDeclineProposed = deal.declineStatus === "DeclineProposed";

  function handleDragStart(e: DragEvent<HTMLDivElement>) {
    if (!draggable) return;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(
      "application/x-phoenix-deal",
      JSON.stringify({ id: deal.id, stage: deal.stage, subState: deal.subState }),
    );
    setDragging(true);
  }

  function handleDragEnd() {
    setDragging(false);
  }

  function handleContextMenu(e: MouseEvent<HTMLDivElement>) {
    if (visibleActions.length === 0) return;
    e.preventDefault();
    setMenuOpen(true);
  }

  return (
    <div
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onContextMenu={handleContextMenu}
      className={cn(
        "group relative bg-white-pure border border-border-light rounded-md p-space-3",
        "shadow-sm transition-all duration-fast ease-standard overflow-hidden",
        "hover:border-gold hover:shadow-md",
        draggable && "cursor-grab active:cursor-grabbing",
        dragging && "opacity-40",
        isDeclined && "opacity-50",
      )}
      data-testid={`deal-card-${deal.dealNumber}`}
      data-stage={deal.stage}
      data-substate={deal.subState}
      data-decline-status={deal.declineStatus ?? "Active"}
    >
      {isDeclineProposed && (
        <div className="mb-space-2 -mt-space-1 inline-flex items-center rounded-pill bg-amber-100 px-space-2 py-[2px] text-caption font-display uppercase tracking-label text-amber-900">
          Proposed Decline
        </div>
      )}
      {/* Floating top-right block: SLA badge + indicator dots + menu.
       *  The badge is the only text inside it; everything else is an icon
       *  or a dot, so the block fits within the 320px Kanban column even
       *  on the worst-case "Nd overdue" label. The title row below
       *  reserves right-padding so the badge can never overlap it. */}
      <div className="absolute top-space-2 right-space-2 flex items-center gap-[3px] max-w-[60%]">
        <Badge
          status={ageBadgeStatus(severity)}
          className="truncate"
          title={`${slaText} in ${labelForSubState(deal.subState)}`}
          aria-label={`${slaText} in ${labelForSubState(deal.subState)}`}
        >
          {severity === "warning" && (
            <AlertTriangle size={10} aria-hidden className="shrink-0" />
          )}
          <span className="truncate">{slaText}</span>
        </Badge>
        {hasOverdueDocs && (
          <span
            className="inline-flex h-2 w-2 rounded-full bg-status-error ring-2 ring-status-error/20"
            title="Overdue document requests"
            aria-label="Overdue documents"
          />
        )}
        {hasMissingRequiredFields && (
          <span
            className="inline-flex h-2 w-2 rounded-full bg-status-warning ring-2 ring-status-warning/20"
            title="Missing required intake fields"
            aria-label="Missing required fields"
          />
        )}
        {ready && !hasOverdueDocs && !hasMissingRequiredFields && (
          <span
            className="inline-flex h-2 w-2 rounded-full bg-status-success ring-2 ring-status-success/20"
            title="Ready to advance"
            aria-label="Ready to advance"
          />
        )}
        {visibleActions.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setMenuOpen((v) => !v);
            }}
            className="ml-space-1 p-[2px] rounded hover:bg-white-warm text-gray-mid hover:text-onyx"
            aria-label="Card actions"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <MoreVertical size={14} aria-hidden />
          </button>
        )}
      </div>

      {/* Reserve enough right-padding for the floating block so the
       *  deal number / assignee never collides with the badge. */}
      <div className="flex items-center gap-space-2 mb-space-1 pr-[110px] min-w-0">
        <Link
          to={`/pipeline/${deal.id}`}
          className="font-mono text-caption text-gold-shadow hover:text-gold-highlight"
          onClick={(e) => e.stopPropagation()}
        >
          {deal.dealNumber}
        </Link>
        {assigneeName && (
          <span className="text-caption text-gray-mid truncate" title={assigneeName}>
            · {assigneeName}
          </span>
        )}
      </div>

      <Link
        to={`/pipeline/${deal.id}`}
        className={cn(
          "block font-display font-bold text-small text-onyx leading-tight hover:text-gold-shadow truncate",
          isDeclined && "line-through",
        )}
        onClick={(e) => e.stopPropagation()}
        title={dealLabel(deal)}
      >
        {dealLabel(deal)}
      </Link>

      {(isDeclined || deal.dealType === "OwnerRecapitalization") && (
        <div className="mt-space-1 flex flex-wrap items-center gap-space-1">
          {isDeclined && (
            <Badge status="error" title={deal.declineProposedReason ?? undefined}>
              Declined
            </Badge>
          )}
          <DealTypeBadge dealType={deal.dealType} />
        </div>
      )}

      {brokerName && (
        <div className="mt-space-1 text-caption truncate" title={brokerName}>
          <BrokerRecordLink
            brokerId={deal.brokerId}
            label={brokerName}
            className="text-gray-mid hover:text-gold-shadow hover:underline"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {deal.stagingSubmissionId && (
        <Link
          to={`/deal-flow/deal-staging/${deal.stagingSubmissionId}`}
          className="mt-space-1 inline-block text-caption text-gold-shadow hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          View staging
        </Link>
      )}

      {/* Amount on its own row — no longer competing with the SLA badge
       *  for horizontal space. */}
      <div className="mt-space-2">
        <span className="font-mono text-caption text-onyx tabular-nums">
          {formatCurrency(deal.purchasePrice)}
        </span>
      </div>

      {menuOpen && visibleActions.length > 0 && (
        <ContextMenu
          actions={visibleActions}
          deal={deal}
          onClose={() => setMenuOpen(false)}
        />
      )}
    </div>
  );
}

function labelForSubState(s: string): string {
  // Match the styles used elsewhere; SUB_STATE_LABEL is in @/types/deals
  // but inlined here to avoid a circular import surface.
  return s.replace(/([A-Z])/g, " $1").trim();
}

interface ContextMenuProps {
  actions: DealCardAction[];
  deal: DealListItemDto;
  onClose: () => void;
}

function ContextMenu({ actions, deal, onClose }: ContextMenuProps) {
  return (
    <div
      role="menu"
      onMouseLeave={onClose}
      className={cn(
        "absolute top-[26px] right-space-2 z-10 min-w-[180px]",
        "bg-white-pure border border-border-light rounded-md shadow-lg",
        "py-space-1",
      )}
    >
      {actions.map((a) => (
        <button
          key={a.key}
          type="button"
          role="menuitem"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
            a.onClick(deal);
          }}
          className={cn(
            "w-full text-left px-space-3 py-space-1 text-small",
            "hover:bg-white-warm",
            a.destructive ? "text-status-error" : "text-onyx",
          )}
        >
          <span className="inline-flex items-center gap-space-2">
            {a.destructive ? (
              <AlertCircle size={12} aria-hidden />
            ) : (
              <Circle size={6} aria-hidden className="opacity-40" />
            )}
            {a.label}
          </span>
        </button>
      ))}
    </div>
  );
}

